import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import jameelFont from "../assets/fonts/jameelnoorinastaliqfont";
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint, faSearch, faFileExcel, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import JsBarcode from 'jsbarcode';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Fuse from 'fuse.js';
import '../styles/armsform.css';

const FormList = () => {
    const navigate = useNavigate();
    const [gdsrEntries, setGdsrEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);

    // Calculate new entries count
    const newEntriesCount = useMemo(() => {
        return gdsrEntries.filter(entry => entry.isNew).length;
    }, [gdsrEntries]);

    // Fuse.js for fuzzy search
    const fuse = useMemo(() => new Fuse(gdsrEntries, {
        keys: ['sr_no', 'token_id', 'book_no', 'date', 'name_news', 'crime', 'thana'],
        threshold: 0.3,
        includeScore: true
    }), [gdsrEntries]);

    const fetchEntries = useCallback(async () => {
        try {
            const response = await axios.post('https://chaman.gob.pk/eac_backend/form.php', {
                action: 'get_records',
            });

            if (Array.isArray(response.data)) {
                const now = new Date();
                setLastRefreshTime(now);

                // Parse and attach timestamp, then sort by newest date+time
                const sortedEntries = response.data
                    .map(entry => {
                        // Try to parse the date reliably
                        const timestamp = new Date(entry.date);
                        return {
                            ...entry,
                            timestamp: isNaN(timestamp.getTime()) ? new Date(0) : timestamp
                        };
                    })
                    .sort((a, b) => b.timestamp - a.timestamp); // Newest first

                setGdsrEntries(prevEntries => {
                    const existingIds = new Set(prevEntries.map(item => item.token_id));

                    const newEntries = sortedEntries
                        .filter(entry => !existingIds.has(entry.token_id))
                        .map(entry => ({ ...entry, isNew: true }));

                    const existingEntries = prevEntries
                        .map(entry => ({ ...entry, isNew: false }));

                    return [...newEntries, ...existingEntries];
                });
            } else {
                setGdsrEntries([]);
                console.error('Expected array but got:', response.data);
            }
        } catch (err) {
            setError('Failed to fetch Form entries');
            console.error(err);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    useEffect(() => {
        // Initial load with small delay to show loading state
        const timer = setTimeout(() => {
            fetchEntries();
        }, 5);

        return () => clearTimeout(timer);
    }, [fetchEntries]);

    const refreshData = () => {
        setIsRefreshing(true);
        fetchEntries();
    };

    const filteredEntries = useMemo(() => {
        if (!searchTerm) return gdsrEntries;
        return fuse.search(searchTerm).map(result => result.item);
    }, [gdsrEntries, searchTerm, fuse]);

    const handleDelete = async (token_id) => {
        if (window.confirm(`Are you sure you want to delete the entry with Token ID: ${token_id}?`)) {
            try {
                const response = await axios.post('https://chaman.gob.pk/eac_backend/form.php', {
                    action: 'delete',
                    token_id,
                });
                if (response.data.status === 'success') {
                    setGdsrEntries(prev => prev.filter(entry => entry.token_id !== token_id));
                } else {
                    throw new Error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                alert(`Error: ${error.message}`);
            }
        }
    };

    const handleEdit = (entry) => {
        navigate('/dashboard/edit-fir', { state: { entry } });
    };

    const handleUrduExport = async (entry) => {
        try {
            const pdf = new jsPDF('l', 'mm', 'legal');

            // Create a temporary div to render Urdu text
            const urduContent = document.createElement('div');

            // Set contentUrdu styles for both pages
            urduContent.style.fontFamily = 'Jameel Noori Nastaleeq, sans-serif';
            urduContent.style.fontSize = '14px';
            urduContent.style.color = 'black';
            urduContent.style.textAlign = 'right';
            urduContent.style.padding = '15px';
            urduContent.style.width = '1000px';  // Adjust width as per requirement
            urduContent.style.margin = '0 auto'
            // ----------------------------- Page 1 Content -----------------------------
            urduContent.innerHTML = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 10px; direction: rtl; margin-bottom:10px;">
        <div id="left" style="flex: 1; text-align: right;">
            <div>چالان نمبر: ${entry.sr_no}</div>
            <div>ابتدائی اطلاع نمبر: ${entry.news_no}</div>
        </div>

        <div id="center" style="flex: 1; text-align: center;">
            <span style="font-size: 30px; font-weight: bold; display: block;">لیویز تھانہ</span>
            <span>فارم نمبر</span> 25_65 (1)
        </div>

        <div id="right" style="flex: 1; text-align: left; margin-top: 10px;">
            <div>مورخہ: ${entry.date}</div>
            <div>مورخہ: ${entry.date}</div>
            <div>ضلع: ${entry.district}</div>
        </div>
    </div>

    <br>
    <table id='firtable' style="width: 100%; border-collapse: collapse; text-align: center; direction: rtl; background-color:white; font-size: 14px;">
        <!-- Header Row -->
        <tr style="font-weight: bold; background-color: white;">
            <td style="padding: 8px; text-align: center; border: 0.5px solid #000; width: 8%;"> نام و پتہ و پیشہ و مستعیث یا اطلاع دہندہ</td>
            <td style="padding: 8px; text-align: center; border: 0.5px solid #000; width: 8%;"> نام و سکونت ملزمان جن کا چالان نہیں کیا گیا۔ خواہ وہ گرفتار ہوئے ہوں یا نہ بشمول مفرادارن</td>
            <td colspan="2" style="padding: 20px; text-align: center; border: 0.5px solid #000; width: 18%; margin-bottom:0px;"> نام ملزمان جن کا چالان کیا گیا
                <table style="width: 100%; border: none; margin-top: 5px;">
                    <tr height="5px">
                        <th style="padding: 10px; height:1px; width: 5%; text-align: center; border: 0 solid #000; background-color:white; margin-top:10px;">زیر حراست</th>
                        <th style="padding: 10px; height:1px; width: 5%; text-align: center; border: 0 solid #000;  margin-top:10px; background-color:white;">ضمانت یا مچلکہ پر</th>
                    </tr>
                </table>
            </td>
            <td style="padding: 8px; text-align: center; border: 0.5px solid #000; width: 11%;"> مال معہ ہتھیار باز یافتہ مقام ووقت برآمدگی کس نے برآمد کیا عدالت میں بھیجا گیا یا نہیں</td>
            <td style="padding: 8px; text-align: center; border: 0.5px solid #000; width: 12%;"> نام وپتہ گواہان</td>
            <td style="padding: 16px; flex:1; text-align: center; border: 0.5px solid #000; width: 50%; height: 100px; vertical-align: top;">
            <br>
      الزام یا اطلاع ــــــــــــــــــــــــــــــــــــــــ 
    <br>
    ${entry.name} بنام
    <br>
    جرم  ${entry.crime} و حالات متعلقہ مقدمہ مختصرا بیان کرو یہ بھی بیان کرو کہ قانون کی کس دفعہ کے تحت الزام عائد کیا گیا ہے
   </td>

        </tr>
        
        <!-- First Data Row -->
        <tr>
            <td style="padding: 1px 0px; text-align: center; border: 0.5px solid #000; height: 5px; overflow: hidden; vertical-align: top;">1</td>
            <td style="padding: 1px; text-align: center; border: 0.5px solid #000; height: 5px; overflow: hidden; vertical-align: top;">2</td>
            <td style="padding: 1px; text-align: center; border: 0.5px solid #000; height: 5px; overflow: hidden; vertical-align: top;">3</td>
            <td style="padding: 1px; text-align: center; border: 0.5px solid #000; height: 5px; overflow: hidden; vertical-align: top;">4</td>
            <td style="padding: 1px; text-align: center; border: 0.5px solid #000; height: 5px; overflow: hidden; vertical-align: top;">5</td>
            <td style="padding: 1px; text-align: center; border: 0.5px solid #000; height: 5px; overflow: hidden; vertical-align: top;">6</td>
            <td style="padding: 1px; text-align: center; border: 0.5px solid #000; height: 5px; overflow: hidden; vertical-align: top;">7</td>
        </tr>

        <!-- Second Data Row (Additional Row) -->
        <tr>
            <td style="padding: 3px; text-align: right; border: 0.5px solid #000; height: 250px; overflow: hidden; vertical-align: top;"> ${entry.name_news}</td>
            <td style="padding: 3px; text-align: right; border: 0.5px solid #000; height: 250px; overflow: hidden; vertical-align: top;">${entry.criminal_loc}</td>
            <td style="padding:3px; text-align: right; border: 0.5px solid #000; height: 250px; overflow: hidden; vertical-align: top;">${entry.under_inquiry}</td>
            <td style="padding: 3px; text-align: right; border: 0.5px solid #000; height: 250px; overflow: hidden; vertical-align: top;">${entry.paid_quiry}</td>
            <td style="padding: 3px; text-align: right; border: 0.5px solid #000; height: 250px; overflow: hidden; vertical-align: top;">${entry.weapon}</td>
            <td style="padding: 3px; text-align: right; border: 0.5px solid #000; height: 250px; overflow: hidden; vertical-align: top;">${entry.witness}</td>
            <td style="padding:3px; text-align: right; border: 0.5px solid #000; height: 250px; overflow: hidden; vertical-align: top;">${entry.remarks}</td>
        </tr>
    </table>
`;

        



            // <div style="direction: rtl; text-align: justify;">ابتدائی اطلاع: ${entry.remarks}</div> 
            document.body.appendChild(urduContent);

            // Capture a higher-resolution image using html2canvas with increased scale
            const canvas = await html2canvas(urduContent, { scale: 3 }); // Higher scale for sharper text
            const imgData = canvas.toDataURL('image/png');

            // Calculate dimensions based on canvas resolution to prevent distortion
            const imgWidth = 315 ; // Width in mm for PDF
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            const barcodeCanvas = document.createElement('canvas');
            JsBarcode(barcodeCanvas, entry.token_id, { format: 'CODE128', displayValue: false, fontSize: 10 });

            // Convert canvas to image URL
            const barcodeDataUrl = barcodeCanvas.toDataURL('image/png');

            // Calculate the width and position for centering the barcode
            const barcodeWidth = 35; // Adjust this to make the barcode smaller
            const barcodeHeight = 15; // Adjust the height if needed
            const centerX = (pdf.internal.pageSize.width - barcodeWidth) / 2; // Center horizontally

            // ----------------------------- Page 1 Content -----------------------------
            // Add the first page content (Urdu text) to the PDF
            pdf.addImage(imgData, 'PNG', 20, 10, imgWidth, imgHeight);

            // Add the barcode image to the first page (if needed)
            pdf.addImage(barcodeDataUrl, 'PNG', centerX, pdf.internal.pageSize.height - 30, barcodeWidth, barcodeHeight);

            // ----------------------------- Page 2 Content -----------------------------
            // Add a new page after the first page
            // Export the PDF
            pdf.save('challan_report.pdf');
        } catch (error) {
            console.error("Error exporting PDF: ", error);
        }
    };

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(gdsrEntries);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Form License Entries');
        XLSX.writeFile(workbook, 'Form.xlsx');
    };

    const Row = ({ index, style }) => {
        const entry = filteredEntries[index];
        if (!entry) return null;

        return (
            <div style={style} className="firtable-row" dir="rtl">
                {/* <div className="table-cell">{entry.sr_no}</div> */}
                <div className="table-cell">{entry.token_id}</div>
                {/* <div className="table-cell">{entry.book_no}</div> */}
                <div className="table-cell">{entry.date}</div>
                {/* <div className="table-cell">{entry.name_news}</div> */}
                <div className="table-cell">{entry.crime}</div>
                <div className="table-cell">{entry.thana}</div>
                <div className="table-cell actions btn-group">
                    <button onClick={() => handleUrduExport(entry)} title="Print" className="print-btn">
                        <FontAwesomeIcon icon={faPrint} />
                    </button>
                    <button onClick={() => handleDelete(entry.token_id)} title="Delete" className="delete-btn">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
            </div>
        );
    };

    if (loading) return (
        <div className="loading-container">
            <div className="modern-loading">
                <div className="loading-wave">
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                </div>
                <p className="loading-text">Loading ...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="error-container">
            <p>Error: {error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
        </div>
    );

    return (
        <div className="certificate-dashboard">
          <div className="dashboard-header">
                          <div className="header-left">
                              {/* <h2>Local Certificate Entries</h2> */}
                              <span className="entries-count">Total Records {gdsrEntries.length}</span>
                          </div>
          
                          <div className="header-right">
                              <div className="search-refresh-wrapper">
                                  <button
                                      onClick={refreshData}
                                      className="refresh-button"
                                      disabled={isRefreshing}
                                      aria-label="Refresh data"
                                  >
                                      <FontAwesomeIcon
                                          icon={faSyncAlt}
                                          spin={isRefreshing}
                                          className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}
                                      />
                                  </button>
          
                                  <div className="search-bar">
                                      <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                      <input
                                          type="text"
                                          placeholder="Search FIRs..."
                                          value={searchTerm}
                                          onChange={(e) => setSearchTerm(e.target.value)}
                                          className="search-input"
                                      />
                                      {searchTerm && (
                                          <button className="clear-search" onClick={() => setSearchTerm('')}>
                                              ×
                                          </button>
                                      )}
                                  </div>
                              </div>
                          </div>
                      </div>

            <div className="action-buttons">
                {/* <button
                    onClick={exportToExcel}
                    className="excel-btn"
                >
                    <FontAwesomeIcon icon={faFileExcel} /> ایکسل میں ایکسپورٹ کریں
                </button> */}
            </div>

            <div className="table-container">
                <div className="firtable-header" dir="rtl">
                    {/* <div className="header-cell">سیریل نمبر</div> */}
                    {/* <div className="header-cell">ٹوکن آئی ڈی</div> */}
                    <div className="header-cell">ٹوکن آئی ڈی</div>
                    {/* <div className="header-cell">بوک نمبر</div> */}
                    <div className="header-cell">تاریخ</div>
                    <div className="header-cell">نام و اطلاع دہندہ</div>
                    {/* <div className="header-cell">کیفیت جرم</div> */}
                    <div className="header-cell">تھانہ</div>
                    <div className="header-cell">ایکشن</div>
                </div>

                <div className="firtable-body">
                    {filteredEntries.length > 0 ? (
                        <AutoSizer>
                            {({ height, width }) => (
                                <List
                                    height={height}
                                    itemCount={filteredEntries.length}
                                    itemSize={45}
                                    width={width}
                                >
                                    {Row}
                                </List>
                            )}
                        </AutoSizer>
                    ) : (
                        <div className="empty-state">
                            <p>No entries found {searchTerm ? `matching "${searchTerm}"` : 'available'}</p>
                        </div>
                    )}
                </div>

                <div className="table-footer">
                    Showing {filteredEntries.length} of {gdsrEntries.length} entries
                </div>
            </div>
        </div>
    );
};

export default FormList;